import React from 'react';

import { type VariantProps, tv } from 'tailwind-variants';

import { cn } from 'common/style/utils';

import { forwardRefNoClassName } from './common';

import type { LucideProps } from 'lucide-react';

export const inputVariants = tv({
  slots: {
    base: ['flex w-full gap-1 items-center box-border bg-transparent dark:bg-input/30'],
    icon: 'text-secondary-foreground pointer-events-none shrink-0',
    input: 'h-full w-full p-0 border-none outline-none bg-transparent placeholder:text-placeholder',
    prefix: 'flex items-center text-secondary-foreground',
    suffix: 'flex items-center text-secondary-foreground',
  },
  variants: {
    variant: {
      default: {
        base: 'input-border input-disabled',
      },
      opaque: {
        base: 'input-border input-disabled bg-background',
      },
      unstyled: {
        base: 'bg-transparent dark:bg-transparent',
      },
    },
    size: {
      small: {
        base: 'h-7 px-2 rounded-sm',
        input: 'text-base',
      },
      default: {
        base: 'h-8 px-3 rounded-sm',
        input: 'text-base',
      },
      large: {
        base: 'h-11 px-4 rounded text-lg',
        input: '',
      },
    },
  },
  compoundVariants: [
    {
      variant: 'unstyled',
      class: {
        base: 'h-auto border-none py-0 px-0',
      },
    },
    {
      size: 'small',
      class: {
        icon: 'size-3.5',
      },
    },
    {
      size: 'default',
      class: {
        icon: 'size-4',
      },
    },
    {
      size: 'large',
      class: {
        base: 'gap-2',
        icon: 'size-4',
      },
    },
  ],
  compoundSlots: [
    {
      slots: ['base', 'input', 'prefix', 'suffix'],
      size: 'default',
      class: 'text-base leading-4.5',
    },
    {
      slots: ['base', 'input', 'prefix', 'suffix'],
      size: 'large',
      class: 'text-lg leading-6',
    },
  ],
  defaultVariants: {
    size: 'default',
    variant: 'default',
  },
});

export type InputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'size' | 'prefix'
> &
  VariantProps<typeof inputVariants> & {
    onChange?: (value: string) => void;
    startIcon?: React.ComponentType<LucideProps>;
    prefix?: React.ReactNode;
    suffix?: React.ReactNode;
  };

const Input = forwardRefNoClassName<HTMLInputElement, InputProps>(
  (
    {
      onChange,
      startIcon: StartIcon,
      type = 'text',
      size,
      prefix,
      suffix,
      variant,
      autoFocus,
      ...props
    },
    ref,
  ) => {
    const styles = inputVariants({
      size,
      variant,
    });

    return (
      <div className={cn(styles.base())}>
        {StartIcon ? <StartIcon className={cn(styles.icon())} /> : null}
        {prefix && <div className={cn(styles.prefix())}>{prefix}</div>}
        <input
          ref={ref}
          className={cn(styles.input())}
          onChange={(e) => onChange?.(e.target.value)}
          type={type}
          autoFocus={autoFocus}
          {...props}
        />
        {suffix && <div className={cn(styles.suffix())}>{suffix}</div>}
      </div>
    );
  },
);

export default Input;
